const TRIGGER_SELECTOR = '[data-check-eligibility-trigger]';
const SSG_REGEX = /^\d{8,20}$/;

import {
  clearMemberVerification,
  shouldClearMemberVerification,
} from './member-verification-utils.js';

export class CheckEligibilityModal extends HTMLElement {
  constructor() {
    super();
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleOverlayClick = this._handleOverlayClick.bind(this);
    this._handleSubmit = this._handleSubmit.bind(this);
    this._handleInputChange = this._handleInputChange.bind(this);
    this._handleStatusIconClick = this._handleStatusIconClick.bind(this);
    this._handleStatusIconKeydown = this._handleStatusIconKeydown.bind(this);
    this._statusResetTimeout = null;
  }

  connectedCallback() {
    this._parseConfig();
    this._cacheElements();
    this._bindEvents();
    this._checkInitialState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleDocumentClick);
    document.removeEventListener('keydown', this._handleKeydown);
    this.removeEventListener('click', this._handleOverlayClick);
  }

  _parseConfig() {
    const configEl = this.querySelector('[data-check-eligibility-config]');
    if (configEl) {
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('CheckEligibilityModal: Failed to parse config', e);
        this.config = {};
      }
    } else {
      this.config = {};
    }
  }

  _cacheElements() {
    this.panel = this.querySelector('.check-eligibility-modal__panel');
    this.closeButton = this.querySelector('[data-close-button]');
    this.ssgInput = this.querySelector('[data-ssg-input]');
    this.fieldError = this.querySelector('[data-field-error]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.submitText = this.querySelector('.check-eligibility-modal__submit-text');
    this.submitLoading = this.querySelector('.check-eligibility-modal__submit-loading');
    this.formSection = this.querySelector('[data-form-section]');
    this.statusSection = this.querySelector('[data-status-section]');
    this.statusIcon = this.querySelector('[data-status-icon]');
    this.statusMessage = this.querySelector('[data-status-message]');
    this.verifiedSection = this.querySelector('[data-verified-section]');
    this.verifiedSsg = this.querySelector('[data-verified-ssg]');
    this.verifiedTier = this.querySelector('[data-verified-tier]');
    this.footerSection = this.querySelector('[data-footer-section]');
  }

  _bindEvents() {
    this.closeButton?.addEventListener('click', () => this._attemptClose());
    this.addEventListener('click', this._handleOverlayClick);
    document.addEventListener('click', this._handleDocumentClick);
    document.addEventListener('keydown', this._handleKeydown);
    this.submitButton?.addEventListener('click', this._handleSubmit);
    this.statusIcon?.addEventListener('click', this._handleStatusIconClick);
    this.statusIcon?.addEventListener('keydown', this._handleStatusIconKeydown);
    this.ssgInput?.addEventListener('input', this._handleInputChange);
    this.ssgInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this._handleSubmit();
      }
    });
  }

  _checkInitialState() {
    const isVerified = CheckEligibilityModal.isVerified(this.config.cookieName);
    const blockStorefront = this.dataset.blockStorefront === 'true';

    this._isVerified = isVerified;

    if (isVerified) {
      this._loadVerifiedState();
      return;
    }

    if (blockStorefront && !shouldClearMemberVerification()) {
      this.show(null, true);
    }
  }

  _loadVerifiedState() {
    const ssg = CheckEligibilityModal.getCookie('sandals_ssg');
    const tier = CheckEligibilityModal.getCookie('sandals_member_tier');

    if (ssg && this.verifiedSsg) {
      this.verifiedSsg.textContent = `SSG: ${ssg}`;
    }
    if (tier && this.verifiedTier) {
      this.verifiedTier.textContent = `Tier: ${tier}`;
    }

    this._isVerified = true;
  }

  show(opener, isAutoOpen = false) {
    this._isVerified = CheckEligibilityModal.isVerified(this.config.cookieName);
    this.openedBy = opener;
    this._isAutoOpen = isAutoOpen;
    const blockStorefront = this.dataset.blockStorefront === 'true';

    if (this._isVerified) {
      this._showVerifiedState();
    } else {
      this._showFormState();
    }

    if (isAutoOpen && blockStorefront) {
      this.closeButton?.setAttribute('hidden', '');
    } else {
      this.closeButton?.removeAttribute('hidden');
    }

    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');

    setTimeout(() => {
      if (!this._isVerified) {
        this.ssgInput?.focus();
      }
    }, 100);
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.openedBy?.focus?.();
    this.openedBy = null;
    this._isAutoOpen = false;

    this._clearStatusResetTimeout();
    this._clearFieldError();
    this._resetFormState();
  }

  _attemptClose() {
    const blockStorefront = this.dataset.blockStorefront === 'true';

    if (this._isAutoOpen && blockStorefront && !this._isVerified) {
      return;
    }

    this.hide();
  }

  _showFormState() {
    this._clearStatusResetTimeout();
    this.formSection?.removeAttribute('hidden');
    this.statusSection?.setAttribute('hidden', '');
    this.verifiedSection?.setAttribute('hidden', '');
    this.footerSection?.removeAttribute('hidden');
    this._disableStatusIconButton();
  }

  _showVerifiedState() {
    this.formSection?.setAttribute('hidden', '');
    this.statusSection?.setAttribute('hidden', '');
    this.verifiedSection?.removeAttribute('hidden');
    this.footerSection?.setAttribute('hidden', '');
  }

  _showStatusState(type, message) {
    this.formSection?.setAttribute('hidden', '');
    this.statusSection?.removeAttribute('hidden');
    this.verifiedSection?.setAttribute('hidden', '');

    this.statusSection?.setAttribute('data-status-type', type);

    if (this.statusIcon) {
      if (type === 'loading') {
        this._disableStatusIconButton();
        this.statusIcon.innerHTML = `
          <svg class="check-eligibility-modal__spinner check-eligibility-modal__spinner--large" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-dasharray="31.4 31.4" />
          </svg>
        `;
      } else if (type === 'success') {
        this._disableStatusIconButton();
        this.statusIcon.innerHTML = `
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#22c55e"/>
            <path d="M14 24L21 31L34 18" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      } else if (type === 'error') {
        this._enableStatusIconButton();
        this.statusIcon.innerHTML = `
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#dc4352"/>
            <path d="M16 16L32 32M32 16L16 32" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      }
    }

    if (this.statusMessage) {
      this.statusMessage.textContent = message;
    }

    if (type === 'error') {
      this.footerSection?.removeAttribute('hidden');
    } else {
      this.footerSection?.setAttribute('hidden', '');
    }
  }

  _resetFormState() {
    if (this.submitButton) {
      this.submitButton.disabled = false;
    }
    if (this.submitText) {
      this.submitText.removeAttribute('hidden');
    }
    if (this.submitLoading) {
      this.submitLoading.setAttribute('hidden', '');
    }
  }

  _setLoadingState(isLoading) {
    if (this.submitButton) {
      this.submitButton.disabled = isLoading;
    }
    if (this.submitText) {
      if (isLoading) {
        this.submitText.setAttribute('hidden', '');
      } else {
        this.submitText.removeAttribute('hidden');
      }
    }
    if (this.submitLoading) {
      if (isLoading) {
        this.submitLoading.removeAttribute('hidden');
      } else {
        this.submitLoading.setAttribute('hidden', '');
      }
    }
    if (this.ssgInput) {
      this.ssgInput.disabled = isLoading;
    }
  }

  _showFieldError(message) {
    if (this.fieldError) {
      this.fieldError.textContent = message;
      this.fieldError.removeAttribute('hidden');
    }
    this.ssgInput?.classList.add('check-eligibility-modal__input--error');
  }

  _clearFieldError() {
    if (this.fieldError) {
      this.fieldError.textContent = '';
      this.fieldError.setAttribute('hidden', '');
    }
    this.ssgInput?.classList.remove('check-eligibility-modal__input--error');
  }

  _handleInputChange() {
    this._clearFieldError();
  }

  _handleStatusIconClick() {
    if (this.statusSection?.getAttribute('data-status-type') !== 'error') return;
    this._dismissErrorStatus();
  }

  _handleStatusIconKeydown(event) {
    if (this.statusSection?.getAttribute('data-status-type') !== 'error') return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this._dismissErrorStatus();
  }

  _enableStatusIconButton() {
    if (!this.statusIcon) return;

    this.statusIcon.setAttribute('role', 'button');
    this.statusIcon.setAttribute('tabindex', '0');
    this.statusIcon.setAttribute('aria-label', 'Dismiss error message');
  }

  _disableStatusIconButton() {
    if (!this.statusIcon) return;

    this.statusIcon.removeAttribute('role');
    this.statusIcon.removeAttribute('tabindex');
    this.statusIcon.removeAttribute('aria-label');
  }

  _clearStatusResetTimeout() {
    if (!this._statusResetTimeout) return;

    clearTimeout(this._statusResetTimeout);
    this._statusResetTimeout = null;
  }

  _scheduleErrorReset(delayMs = 10000) {
    this._clearStatusResetTimeout();
    this._statusResetTimeout = setTimeout(() => {
      this._dismissErrorStatus();
    }, delayMs);
  }

  _dismissErrorStatus() {
    this._clearStatusResetTimeout();
    this._showFormState();
    this.ssgInput?.focus();
    this.ssgInput?.select();
  }

  async _handleSubmit() {
    this._clearFieldError();

    const ssgValue = this.ssgInput?.value?.trim() || '';

    if (!SSG_REGEX.test(ssgValue)) {
      this._showFieldError(this.config.validationErrorMessage || 'Please enter a valid member ID (8-20 digits).');
      this.ssgInput?.focus();
      return;
    }

    if (!this.config.middlewareUrl) {
      this._showFieldError('Configuration error: Middleware URL not set.');
      return;
    }

    this._setLoadingState(true);

    try {
      const response = await fetch(this.config.middlewareUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ input: ssgValue }),
      });

      if (!response.ok) {
        console.warn('CheckEligibilityModal: Verification returned non-OK response', response.status);
        this._handleInvalid();
        return;
      }

      const data = await response.json();

      if (data?.data?.valid === true) {
        await this._handleSuccess(data.data);
      } else {
        this._handleInvalid();
      }
    } catch (error) {
      console.error('CheckEligibilityModal: Verification failed', error);
      this._handleNetworkError();
    }
  }

  async _handleSuccess(data) {
    const { ssg, member_tier } = data;

    this._setCookies(ssg, member_tier);

    await this._setCartAttributes(ssg, member_tier);

    this._dispatchVerifiedEvent(ssg, member_tier);

    this._showStatusState('success', this.config.successMessage || 'Membership verified!');

    this._isVerified = true;
    this._loadVerifiedState();

    const delay = this.config.successDelayMs || 2000;
    setTimeout(() => {
      this.hide();
    }, delay);
  }

  _handleInvalid() {
    this._setLoadingState(false);
    this._showStatusState('error', this.config.invalidMessage || 'We could not verify your membership.');
    this._scheduleErrorReset(10000);
  }

  _handleNetworkError() {
    this._setLoadingState(false);
    this._showStatusState('error', this.config.networkErrorMessage || 'Something went wrong. Please try again.');
    this._scheduleErrorReset(10000);
  }

  _setCookies(ssg, memberTier) {
    const isSecure = window.location.protocol === 'https:';
    const cookieOptions = `Path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`;

    document.cookie = `sandals_verified=true; ${cookieOptions}`;
    document.cookie = `sandals_ssg=${encodeURIComponent(ssg)}; ${cookieOptions}`;
    document.cookie = `sandals_member_tier=${encodeURIComponent(memberTier)}; ${cookieOptions}`;
  }

  async _setCartAttributes(ssg, memberTier) {
    const root = this.config.shopifyRoutesRoot || window.Shopify?.routes?.root || '/';
    const url = `${root}cart/update.js`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'SSG': ssg,
            'Member Tier': memberTier,
          },
        }),
      });

      if (!response.ok) {
        console.error('CheckEligibilityModal: Failed to set cart attributes', response.status);
      }

      if (window.liquidAjaxCart?.update) {
        window.liquidAjaxCart.update({}, {});
      }
    } catch (error) {
      console.error('CheckEligibilityModal: Error setting cart attributes', error);
    }
  }

  _dispatchVerifiedEvent(ssg, memberTier) {
    const event = new CustomEvent('sandals:verified', {
      bubbles: true,
      detail: {
        ssg,
        member_tier: memberTier,
      },
    });
    document.dispatchEvent(event);
  }

  _handleDocumentClick(event) {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    event.preventDefault();
    this.show(trigger, false);
  }

  _handleKeydown(event) {
    if (event.key !== 'Escape' || !this.hasAttribute('open')) return;
    this._attemptClose();
  }

  _handleOverlayClick(event) {
    if (event.target !== this) return;
    this._attemptClose();
  }

  static isVerified(cookieName = 'sandals_verified') {
    return CheckEligibilityModal.getCookie(cookieName) === 'true';
  }

  static getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return decodeURIComponent(parts.pop().split(';').shift());
    }
    return null;
  }

  static async clearVerification() {
    await clearMemberVerification();
  }
}

if (!customElements.get('check-eligibility-modal')) {
  customElements.define('check-eligibility-modal', CheckEligibilityModal);
}

window.CheckEligibilityModal = CheckEligibilityModal;
